import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert, Card, InputGroup, Spinner } from 'react-bootstrap';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import api from '../../services/api';
import styles from './Register.module.css';

interface FieldConfig {
  id?: number;
  field_name: string;
  table_name: string;
  user_type: string;
  is_required: boolean;
  display_order: number;
  field_label: string;
  field_type: string;
  is_visible: boolean;
}

type InputElement = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'customer';
  const [userType, setUserType] = useState<string>(defaultRole);
  const [fieldConfig, setFieldConfig] = useState<FieldConfig[]>([]);
  const [formData, setFormData] = useState<{ [key: string]: any }>({ user_type: defaultRole });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const navigate = useNavigate();

  // Fetch registration field config for the selected user type
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await api.get('/admin/registration-fields/config', {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined
        });
        const config: FieldConfig[] = res.data.filter((c: FieldConfig) => c.user_type === userType && c.is_visible);
        // Sort by display_order if needed
        setFieldConfig(config.sort((a, b) => a.display_order - b.display_order));
        // Initialize formData with empty values for visible fields
        const initialData: { [key: string]: any } = { user_type: userType };
        config.forEach(f => { initialData[f.field_name] = ''; });
        setFormData(initialData);
        setError('');
      } catch (err: any) {
        setError('Failed to load registration fields');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [userType]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<InputElement>) => {
    const { name, value, type } = e.target;
    let newValue: any = value;
    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    }
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
  };

  // Handle user type change (if you want to allow switching roles)
  const handleUserTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setUserType(e.target.value);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      // Only send visible fields
      const submitData: { [key: string]: any } = { user_type: userType };
      fieldConfig.forEach(f => {
        submitData[f.field_name] = formData[f.field_name];
      });
      await axios.post('/ap/api/auth/register', submitData);
      setSuccess('Registration successful! Please check your email.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={styles.card}>
      <Card.Body>
        <h2 className="mb-4">Register</h2>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}
        <Form onSubmit={handleSubmit}>
          {/* Optionally allow switching user type */}
          <Form.Group className="mb-3">
            <Form.Label>Account Type</Form.Label>
            <Form.Select name="user_type" value={userType} onChange={handleUserTypeChange} disabled={loading}>
              <option value="customer">Customer</option>
              <option value="business">Business</option>
              <option value="affiliate">Affiliate</option>
            </Form.Select>
          </Form.Group>
          <Row>
            {fieldConfig.map(field => (
              <Col md={field.field_type === 'textarea' ? 12 : 6} key={field.field_name} className="mb-3">
                <Form.Group>
                  <Form.Label>{field.field_label}</Form.Label>
                  {field.field_type === 'textarea' ? (
                    <Form.Control
                      as="textarea"
                      name={field.field_name}
                      value={formData[field.field_name] || ''}
                      onChange={handleChange}
                      required={field.is_required}
                      disabled={loading}
                    />
                  ) : field.field_type === 'select' ? (
                    <Form.Select
                      name={field.field_name}
                      value={formData[field.field_name] || ''}
                      onChange={handleChange}
                      required={field.is_required}
                      disabled={loading}
                    >
                      <option value="">Select...</option>
                      {/* You can add options here if needed */}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type={field.field_type}
                      name={field.field_name}
                      value={formData[field.field_name] || ''}
                      onChange={handleChange}
                      required={field.is_required}
                      disabled={loading}
                    />
                  )}
                </Form.Group>
              </Col>
            ))}
          </Row>
          <Button type="submit" className="w-100 mt-3" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Register
          </Button>
        </Form>
        <div className="mt-3 text-center">
          Already have an account? <Link to="/login">Login</Link>
        </div>
      </Card.Body>
    </Card>
  );
};

export default Register; 
